import { RootPaths } from "@src/appV2/App/paths";

export const PLACEMENTS_BASE_PATH = "placements";

export const PLACEMENT_WORKPLACE_PROFILE_PATH = "workplaces/:workplaceId/profile";
export const PLACEMENT_WORKPLACE_CLOCK_IN_GUIDE_PATH = "workplaces/:workplaceId/clock-in-guide";
export const PLACEMENT_WORKPLACE_JOBS_PATH = "workplaces/:workplaceId/jobs";
export const PLACEMENT_WORKPLACE_REVIEWS_PATH = "workplace/:workplaceId/reviews";
export const PLACEMENT_WORKPLACE_REVIEWS_COMMENT_PATH = `workplace/:workplaceId/comment/:commentId`;
export const PLACEMENT_WORKPLACE_LEAVE_REVIEW_PATH = `workplace/:workplaceId/reviews/leave-review`;

export const PLACEMENT_REFERRAL_SHARE_LINK_PATH = "referrals";
export const PLACEMENT_HIRING_INSTANT_PAY_TRIAL_PATH = "hiring-instant-pay-trial";
export const PLACEMENT_HIRING_INSTANT_PAY_FORM_PATH = "hiring-instant-pay";
export const PLACEMENT_DISPUTE_BAN_PATH = "dispute-ban/:banId";
export const PLACEMENT_SIGN_JOB_SEARCH_AGREEMENT_PATH = "sign-job-search-agreement";

export const PLACEMENT_PATHS = {
  ROOT: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}`,
  DETAILS: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/:placementId/placement-candidate/:placementCandidateId?`,
  DETAILS_WITHOUT_PLACEMENT_CANDIDATE: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/:placementId`,
  CHAT: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/:placementId/workplace/:workplaceId/chat?source=:source`,
  WORKPLACE_PROFILE: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/${PLACEMENT_WORKPLACE_PROFILE_PATH}?sourcePlacementId=:sourcePlacementId`,
  WORKPLACE_CLOCK_IN_GUIDE: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/${PLACEMENT_WORKPLACE_CLOCK_IN_GUIDE_PATH}?sourcePlacementId=:sourcePlacementId`,
  WORKPLACE_JOBS: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/${PLACEMENT_WORKPLACE_JOBS_PATH}?source=:source`,
  WORKPLACE_REVIEWS: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/${PLACEMENT_WORKPLACE_REVIEWS_PATH}`,
  WORKPLACE_REVIEWS_COMMENT: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/${PLACEMENT_WORKPLACE_REVIEWS_COMMENT_PATH}`,
  WORKPLACE_LEAVE_REVIEW: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/${PLACEMENT_WORKPLACE_LEAVE_REVIEW_PATH}`,
  PLACEMENT_REFERRAL_SHARE_LINK: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/:placementId/${PLACEMENT_REFERRAL_SHARE_LINK_PATH}`,
  HIRING_INSTANT_PAY_TRIAL: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/${PLACEMENT_HIRING_INSTANT_PAY_TRIAL_PATH}`,
  HIRING_INSTANT_PAY_FORM: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/${PLACEMENT_HIRING_INSTANT_PAY_FORM_PATH}`,
  DISPUTE_BAN: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/${PLACEMENT_DISPUTE_BAN_PATH}`,
  SIGN_JOB_SEARCH_AGREEMENT: `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/${PLACEMENT_SIGN_JOB_SEARCH_AGREEMENT_PATH}`,
} as const;

export function getPlacementsRootPath({
  showApplications,
  applications,
  interviews,
}: {
  showApplications?: boolean;
  applications?: boolean;
  interviews?: boolean;
}): string {
  const path = PLACEMENT_PATHS.ROOT;
  const searchParams = new URLSearchParams();
  if (showApplications) {
    searchParams.set("showApplications", showApplications.toString());
  }

  if (applications) {
    searchParams.set("applications", applications.toString());
  }

  if (interviews) {
    searchParams.set("interviews", interviews.toString());
  }

  return `${path}?${searchParams.toString()}`;
}

interface PlacementDetailsPathOptions {
  cardType?: "open-house" | "interview-days";
  section?: "payAndSchedule" | "workplaceLocation";
  autoOpenInterviewBooking?: boolean;
}

export function getPlacementDetailsPath(
  placementId: string,
  placementCandidateId: string,
  options: PlacementDetailsPathOptions = {}
): string {
  const { cardType, section, autoOpenInterviewBooking } = options;

  const path = PLACEMENT_PATHS.DETAILS.replace(":placementId", placementId).replace(
    ":placementCandidateId?",
    placementCandidateId
  );

  const searchParams = new URLSearchParams();
  if (cardType) {
    searchParams.set("cardType", cardType);
  }

  if (section) {
    searchParams.set("section", section);
  }

  if (autoOpenInterviewBooking) {
    searchParams.set("requestEasySchedulingInterview", "true");
    searchParams.set("scheduling-type", "easy-scheduling");
  }

  const queryString = searchParams.toString();
  return queryString ? `${path}?${queryString}` : path;
}

export function getPlacementDetailsPathWithoutPlacementCandidate(placementId: string): string {
  return PLACEMENT_PATHS.DETAILS_WITHOUT_PLACEMENT_CANDIDATE.replace(":placementId", placementId);
}

export function getPlacementChatPath(
  placementId: string,
  workplaceId: string,
  source:
    | "application-success-details"
    | "chat-with-workplace"
    | "chat-with-placement-candidate-via-chat-channels"
    | "chat-with-placement-candidate-via-my-shifts"
    | "chat-with-placement-candidate-via-job-request"
): string {
  return PLACEMENT_PATHS.CHAT.replace(":placementId", placementId)
    .replace(":workplaceId", workplaceId)
    .replace(":source", source);
}

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const WORKPLACE_PLACEMENTS_PATH_SOURCES = {
  WORKPLACE_PROFILE_MODAL: "workplace-profile-modal",
  WORKPLACE_PLACEMENTS_AD_CARD: "workplace-placements-ad-card",
  PLACEMENT_MAP_VIEW: "placement-map-view",
} as const;

export type WorkplacePlacementsPathSource =
  (typeof WORKPLACE_PLACEMENTS_PATH_SOURCES)[keyof typeof WORKPLACE_PLACEMENTS_PATH_SOURCES];

export function getWorkplacePlacementsPath(
  workplaceId: string,
  source: WorkplacePlacementsPathSource
): string {
  return PLACEMENT_PATHS.WORKPLACE_JOBS.replace(":workplaceId", workplaceId).replace(
    ":source",
    source
  );
}

export function getWorkplaceClockInGuidePath(
  workplaceId: string,
  sourcePlacementId?: string
): string {
  const path = PLACEMENT_PATHS.WORKPLACE_CLOCK_IN_GUIDE.replace(":workplaceId", workplaceId);
  return sourcePlacementId
    ? path.replace(":sourcePlacementId", sourcePlacementId)
    : path.replace("?sourcePlacementId=:sourcePlacementId", "");
}

export function getDisputeBanPath(banId: string): string {
  return PLACEMENT_PATHS.DISPUTE_BAN.replace(":banId", banId);
}

export type SignedAtStage = "BOOKING" | "BROWSING_JOBS" | "SIGN_UP";

/**
 * Builds the signing route path with signedAtStage query parameter
 */
export function getSignJobSearchAgreementPath(signedAtStage: SignedAtStage): string {
  return `${PLACEMENT_PATHS.SIGN_JOB_SEARCH_AGREEMENT}?signedAtStage=${signedAtStage}`;
}
